import { IRosterBooking } from '../../types';
import moment from 'moment-timezone';
import { FieldLabel, Text } from 'common-components/typography';
import { Avatar, Icon } from 'antd';
import { HyperlinkButton } from 'common-components/buttons';
import React, { useState } from 'react';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import CommonUtils from 'utilities/common-utils';
import PermissionUtils from 'utilities/permission-utils';
import BookingRemoveWorkerActionModal from 'views/bookings-v2/other/components/BookingRemoveWorkerActionModal';
import _ from 'lodash';
import { BookingAssignWorkerModal } from 'views/bookings-v2/other/components/booking-assign-worker-modal/booking-assign-worker-modal';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import TextTag from 'common-components/tags/TextTag';
import { RecommendedPanel } from '../../components/recommended-panel';
import { useFetchSessionCapacity } from 'stores/hooks/query-hooks/use-query-fetch-session-capacity';
import { useFetchCustomerRatio } from 'stores/hooks/query-hooks/use-query-fetch-customer-ratio';
import { useHistory } from 'react-router-dom';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import { Box, Button, Divider, Group, ScrollArea } from '@good/ui/core';

interface CustomerBookingPopoverProps {
  booking: IRosterBooking;
  refreshAndCloseData: () => void;
  setCanClosePopover: (value: boolean) => void;
}

function CustomerBookingPopover({ booking, refreshAndCloseData, setCanClosePopover }: CustomerBookingPopoverProps) {
  const {
    status,
    startDateTime,
    endDateTime,
    serviceName,
    customerFirstName,
    customerLastName,
    workerFirstName,
    workerLastName,
    workerAttachmentUrl,
    attachmentUrl,
    bookingId,
    serviceId,
    serviceDateTimeId,
  } = booking;

  const [openAssignWorker, setOpenAssignWorker] = useState<boolean>(false);
  const [openRemoveWorker, setOpenRemoveWorker] = useState<boolean>(false);
  const history = useHistory();
  let customerRatio = '1:1';
  const { data: ratioData, isLoading: ratioIsLoading } = useFetchCustomerRatio(
    { bookingId },
    booking.serviceType === ServiceType.GROUP,
  );
  const { data: sessionData, isLoading: sessionIsLoading } = useFetchSessionCapacity(
    {
      serviceId,
      serviceDateTimeId,
    },
    booking.serviceType === ServiceType.GROUP,
  );
  if (booking.serviceType === ServiceType.GROUP) {
    const defaultRatio = ratioData?.data?.defaultRatio;
    const ratios = ratioData?.data?.ratios;

    if (_.isEmpty(ratios)) {
      customerRatio = defaultRatio;
    } else if (ratios.length === 1) {
      customerRatio = !_.isEmpty(ratios) ? ratios[0].teamMemberCustomerRatio : defaultRatio;
    } else {
      customerRatio = ratios;
    }
  }
  const portalUser = usePortalUser();
  const {
    groupServiceStore: groupServiceDispatch,
    navigationStore: navigationDispatch,
    bookingsStore: bookingDispatch,
  } = useDispatch<IRootDispatch>();
  const { setCustomerBookingToOpen } = groupServiceDispatch;
  const { setSelectedSideNavMenuKeys } = navigationDispatch;
  const { doAssignWorker, setSelectedBookingItem } = bookingDispatch;
  const mStartDate = moment(startDateTime);
  const mEndDate = moment(endDateTime);
  const customerFullName = `${customerFirstName} ${customerLastName}`;
  const workerFullName = workerFirstName ? `${workerFirstName} ${workerLastName}` : null;
  const dateDisplay = (
    <>
      <b>{mStartDate.format('dddd, D MMMM YYYY')}</b>
    </>
  );
  const timeDisplay = (
    <>
      <b>{mStartDate.format('h:mm a')}</b> - {mEndDate.format('h:mm a')}
    </>
  );

  const canAssignWorker = !!_.find([ShiftSlotStatus.UNASSIGNED], (status) => status === booking.shiftSlotStatus);
  const canRemoveWorker = !!_.find(
    [ShiftSlotStatus.PENDING, ShiftSlotStatus.CONFIRMED],
    (status) => status === booking.shiftSlotStatus,
  );
  const isCancelled = !!_.find(
    [
      BookingStatus.BUSINESS_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED_WITH_FEE,
      BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
    ],
    (status) => status === booking.status,
  );

  const goToBooking = async () => {
    if (booking.serviceType === ServiceType.GROUP) {
      await setCustomerBookingToOpen({ attendanceId: booking.bookingId });
      history.push(`/group-service/${booking.serviceId}/session/details/${booking.serviceDateTimeId}`, {
        bookingId: booking.bookingId,
      });
      setSelectedSideNavMenuKeys(['/services']);
    } else {
      history.push(`/bookings/details/${booking.bookingId}`, {
        from: { url: '/bookings?view=calendar', linkLabel: 'Back to Calendar' },
      });
      setSelectedSideNavMenuKeys(['/bookings']);
    }
  };

  const goToService = (serviceId: string, serviceType: ServiceType) => {
    setSelectedSideNavMenuKeys(['/services']);
    return serviceType === ServiceType.GROUP
      ? history.push(`/group-service/details/${serviceId}`)
      : history.push(`/service/details/${serviceId}`);
  };

  const goToSession = (serviceId: string, serviceDateTimeId: string) => {
    setSelectedSideNavMenuKeys(['/services']);
    return history.push(`/group-service/${serviceId}/session/details/${serviceDateTimeId}`);
  };

  const openAssignWorkerModal = () => {
    setCanClosePopover(false);
    setOpenAssignWorker(true);
  };

  const closeAssignWorkerModal = (doRefreshData = false) => {
    setCanClosePopover(true);
    setOpenAssignWorker(false);
    doRefreshData && refreshAndCloseData();
  };

  const openRemoveWorkerModal = async () => {
    await setSelectedBookingItem(booking);
    setOpenRemoveWorker(true);
  };

  const doCloseAndRefresh = async (doRefreshData = false) => {
    setCanClosePopover(true);
    setOpenRemoveWorker(false);
    await setSelectedBookingItem({});
    doRefreshData && refreshAndCloseData();
  };

  const onSubmitAssign = async ({
    selectedWorker,
    editRecurringMode,
    shiftSlotStatus,
    isRemovePendingShiftSlots,
    ignoreShiftClashShiftIds,
  }) => {
    const data = {
      bookingId: booking.bookingId,
      bookingRequestId: booking.bookingRequestId,
      supportWorkerId: selectedWorker.supportWorkerId,
      startDateTime: booking.startDateTime,
      isRecurring: booking.isRecurring,
      shiftSlotStatus,
      isRemovePendingShiftSlots,
      editRecurringMode,
      bookingType: booking.bookingType,
      doNotSetBookingItem: true,
      ignoreShiftClashShiftIds,
    };
    await doAssignWorker(data);
  };

  const hasEditWorkerPermissions = PermissionUtils.validatePermission(
    'EditWorkerInBooking',
    portalUser.permissions.permissionRoles,
    booking.serviceDepartmentId,
    booking.serviceId,
  );

  return (
    <>
      <ScrollArea h='100%' p='md'>
        <div className=' flex-column line-height-100 select-none'>
          {booking.address && booking.serviceType === ServiceType.INDIVIDUAL && canAssignWorker && (
            <BookingAssignWorkerModal
              isOpen={openAssignWorker}
              onClose={closeAssignWorkerModal}
              serviceId={booking.serviceId}
              bookingId={booking.bookingId}
              bookingRequestId={booking.bookingRequestId}
              onSubmitAssign={onSubmitAssign}
              isRecurring={booking.isRecurring}
              address={booking.address}
              sessions={[]}
              isEditingBooking={true}
              startDateTime={booking.startDateTime}
              endDateTime={booking.endDateTime}
              customerUserIds={[booking.bookerUserId]}
              displayTimezone={booking.timezone}
              bookingType={booking.bookingType}
            />
          )}
          {hasEditWorkerPermissions && booking.serviceType === ServiceType.INDIVIDUAL && canRemoveWorker && (
            <BookingRemoveWorkerActionModal
              bookingType={booking.bookingType}
              isOpen={openRemoveWorker}
              onClose={doCloseAndRefresh}
            />
          )}
          <div className='inline-block mb-x-small line-height-100'>
            <StatusTag status={status} size='small' />
          </div>
          <div className='line-height-120 mb-medium'>
            <Text size='x-large' lineHeight={135}>
              {dateDisplay}
            </Text>
            <br />
            <Text size='x-large' lineHeight={135}>
              {timeDisplay}
            </Text>
            <br />
            <Text size='x-large' lineHeight={135}>
              <Text size='x-large' color={'secondary'}>
                {CommonUtils.formatDurationString(moment(mStartDate), moment(mEndDate))}
              </Text>
            </Text>

            <div className={'flex-row align-center mt-medium'}>
              {' '}
              <BookingTypeIcon
                type={
                  booking.bookingType === BookingType.ACTIVITY_RECORD
                    ? BookingType.ACTIVITY_RECORD
                    : booking.serviceType
                }
                className={'mr-small'}
              />
              <Text color={'tertiary'}>{CommonUtils.formatServiceTypeStatus(booking)}</Text>
            </div>

            {/* Display service information in logical order for Group Services */}
            {booking.serviceType === ServiceType.GROUP ? (
              <div className='mt-small'>
                {/* Service name */}
                <div className='mb-x-small'>
                  <HyperlinkButton color={'black'} onClick={() => goToService(booking.serviceId, booking.serviceType)}>
                    {serviceName}
                  </HyperlinkButton>
                </div>

                {/* Schedule name */}
                {booking.scheduleName && (
                  <div className='mb-x-small'>
                    <Text size='large' color='secondary'>
                      <Text size='medium' color='secondary'>
                        Schedule:{' '}
                      </Text>
                      <Text weight='bold' color='primary'>
                        {booking.scheduleName}
                      </Text>
                    </Text>
                  </div>
                )}

                {/* Session name (description) */}
                {booking.description && (
                  <div className='mb-x-small'>
                    <Text size='large' color='secondary'>
                      <Text size='medium' color='ocean'>
                        Session:{' '}
                      </Text>
                      <Text weight='bold' color='primary'>
                        {booking.description}
                      </Text>
                    </Text>
                  </div>
                )}

                <div className='mt-small'>
                  <HyperlinkButton onClick={() => goToSession(booking.serviceId, booking.serviceDateTimeId)}>
                    Go to session
                  </HyperlinkButton>
                </div>
              </div>
            ) : (
              /* Individual service display */
              <HyperlinkButton color={'black'} onClick={() => goToService(booking.serviceId, booking.serviceType)}>
                {serviceName}
              </HyperlinkButton>
            )}
          </div>

          {booking.serviceType === ServiceType.GROUP && (
            <div className={'mb-large'}>
              <div className='mt-x-small'>
                {sessionIsLoading ? (
                  <SpinningLoader size={20} message={'Fetching recommended capacity...'} />
                ) : (
                  sessionData &&
                  sessionData.data && (
                    <RecommendedPanel
                      recommendedTotalShiftSlot={sessionData.data.recommendedTotalShiftSlot}
                      totalAssignedShiftSlot={sessionData.data.totalAssignedShiftSlot}
                    />
                  )
                )}
              </div>
            </div>
          )}

          <div className='mb-large'>
            <FieldLabel text={'CUSTOMER'} />
            <div className='flex-row align-center mt-x-small'>
              <Avatar icon='user' className='mr-small' src={attachmentUrl} />
              <Text>{customerFullName}</Text>
            </div>
          </div>

          {booking.serviceType === ServiceType.GROUP && (
            <div className='mb-large'>
              <FieldLabel text={'Customer ratio'} />
              <div className='mt-x-small'>
                {ratioIsLoading ? (
                  <SpinningLoader size={20} message={'Fetching ratios...'} />
                ) : _.isString(customerRatio) ? (
                  <TextTag
                    content={customerRatio}
                    className={'mr-x-small'}
                    size={'regular'}
                    theme={'light'}
                    color={'blue'}
                  />
                ) : (
                  _.map(customerRatio, (ratio: any, index) => {
                    return (
                      <>
                        <div className={`flex-row mt-x-small ${index !== 0 && 'mt-small'} `}>
                          <div className={'mr-small'}>
                            <TextTag
                              content={ratio.teamMemberCustomerRatio}
                              size={'small'}
                              theme={'light'}
                              color={'blue'}
                              className={'inline-block'}
                            />
                          </div>
                          <div>
                            <div style={{ marginTop: '-1px' }}>
                              <Text size={'large'}>
                                {moment.tz(ratio.startDateTime, booking.timezone).format('h:mm a')}
                                {' - '}
                                {moment.tz(ratio.endDateTime, booking.timezone).format('h:mm a')}
                              </Text>
                            </div>
                            {ratio.comments && (
                              <div>
                                <Text color={'secondary'} size={'regular'}>
                                  {ratio.comments}
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {booking.serviceType === ServiceType.GROUP && booking.shiftCustomerAssignments && (
            <div className='mb-large'>
              <FieldLabel text={'TEAM MEMBER'} />
              <div className='mt-x-small' style={{ overflow: 'auto', maxHeight: '120px' }}>
                {_.map(booking.shiftCustomerAssignments, (assignment) => {
                  return (
                    <div className='flex-row align-center mt-x-small'>
                      <div className='flex-row line-height-120 mb-small'>
                        <Avatar icon='user' size='small' className='mr-small' src={assignment.customerAttachmentUrl} />
                        <Text size='regular'>
                          <span className={'text-weight-bold'}>
                            {assignment.supportWorkerFirstName} {assignment.supportWorkerLastName}
                          </span>
                          <br />
                          <span>
                            {moment.tz(assignment.startDateTime, booking.timezone).format('h:mm a')}{' '}
                            {moment.tz(assignment.endDateTime, booking.timezone).format('h:mm a')}
                          </span>
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {booking.serviceType === ServiceType.INDIVIDUAL && (
            <div className='mb-large'>
              <FieldLabel text={'TEAM MEMBER'} />
              <div className='flex-row align-center mt-x-small'>
                {workerFullName ? (
                  <>
                    <Avatar icon='user' className='mr-small' shape='square' src={workerAttachmentUrl} />
                    <Text>{workerFullName}</Text>
                  </>
                ) : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ? (
                  <Text>
                    <Icon type='wifi' className='text-color-violet-light mr-x-small' />
                    Published
                  </Text>
                ) : (
                  <Text color='secondary'>No team member assigned</Text>
                )}
              </div>
            </div>
          )}

          <div className={'mb-large'}>
            <FieldLabel text={'TOTAL'} />
            <div className='flex-row align-center mt-x-small'>
              <Text>{CommonUtils.formatPrice(Number(booking.billingTotal))}</Text>
            </div>
          </div>
        </div>
      </ScrollArea>
      <Box pos='sticky' bottom={0} bg='var(--mantine-color-body)'>
        <Divider />
        <Group gap='xs' justify='end' p='md'>
          <Button onClick={goToBooking}>View booking</Button>
          {hasEditWorkerPermissions &&
            !isCancelled &&
            booking.serviceType === ServiceType.INDIVIDUAL &&
            (canAssignWorker || canRemoveWorker) && (
              <>
                {hasEditWorkerPermissions &&
                  canAssignWorker &&
                  !isCancelled &&
                  ((booking.status !== BookingStatus.ARCHIVED && booking.serviceType === ServiceType.INDIVIDUAL) ||
                    booking.serviceType === ServiceType.GROUP) && (
                    <Button onClick={openAssignWorkerModal} variant='default'>
                      Assign team member
                    </Button>
                  )}
                {hasEditWorkerPermissions &&
                  booking.status &&
                  canRemoveWorker &&
                  ((booking.status !== BookingStatus.ARCHIVED &&
                    booking.serviceType === ServiceType.INDIVIDUAL &&
                    (booking.status === BookingStatus.PENDING ||
                      booking.status === BookingStatus.ACCEPTED ||
                      booking.status === BookingStatus.CONFIRMED)) ||
                    booking.serviceType === ServiceType.GROUP) && (
                    <Button className={'mr-small'} onClick={openRemoveWorkerModal} variant='default'>
                      Remove team member
                    </Button>
                  )}
              </>
            )}
        </Group>
      </Box>
    </>
  );
}

export default CustomerBookingPopover;
